import { Component, computed, inject } from "@angular/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";

import { select } from "@ngxs/store";

import { AccessControlService, ViewScopeService, type ViewScope } from "@dis/auth";
import { LayoutState } from "@dis/shared";

const ALL_KEY = "all";

const keyOf = (scope: ViewScope): string => (scope.kind === "all" ? ALL_KEY : `${scope.kind}:${scope.code}`);

/**
 * Header picker for which Division or Site JTMS shows. Narrows lists, tables, KPIs, dashboards and search through
 * `AccessControlService.viewScope`; lookups and workflows keep the user's full access. Hidden when there's nothing
 * to choose between.
 */
@Component({
  selector: "jtms-view-scope-select",
  imports: [MatFormFieldModule, MatSelectModule, MatTooltipModule],
  host: { "[class.handset]": "isHandset()" },
  template: `
    @if (hasChoices()) {
      <mat-form-field
        appearance="outline"
        subscriptSizing="dynamic"
        class="view-scope"
        [matTooltip]="hint()"
      >
        <mat-label>Viewing</mat-label>
        <mat-select
          [value]="value()"
          (selectionChange)="choose($event.value)"
          [panelWidth]="null"
          aria-describedby="view-scope-hint"
        >
          <mat-select-trigger>{{ triggerLabel() }}</mat-select-trigger>
          <mat-option [value]="allKey">All (my access)</mat-option>
          @for (division of rows(); track division.code) {
            @if (division.selectable) {
              <mat-option class="division-option" [value]="'division:' + division.code">{{ division.label }}</mat-option>
              @for (site of division.sites; track site.code) {
                <mat-option class="site-option" [value]="'site:' + site.code" [attr.aria-label]="site.ariaLabel">
                  {{ site.label }}
                </mat-option>
              }
            } @else {
              <mat-optgroup [label]="division.label">
                @for (site of division.sites; track site.code) {
                  <mat-option [value]="'site:' + site.code" [attr.aria-label]="site.ariaLabel">{{ site.label }}</mat-option>
                }
              </mat-optgroup>
            }
          }
        </mat-select>
      </mat-form-field>
      <span id="view-scope-hint" class="cdk-visually-hidden">{{ hint() }}</span>
    }
  `,
  styles: `
    :host {
      display: flex;
      align-items: center;
    }

    .view-scope {
      width: 10rem;
      font-size: 14px;
      --mat-form-field-container-height: 40px;
      --mat-form-field-container-vertical-padding: 8px;
      /* The default outlined label is 4.49:1 on the header background, just under AA */
      --mat-form-field-outlined-label-text-color: var(--mat-sys-on-surface, rgba(0, 0, 0, 0.87));
    }

    :host(.handset) .view-scope {
      width: 7rem;
    }

    .division-option {
      font-weight: 500;
    }

    .site-option {
      padding-left: 32px;
    }
  `,
})
export class ViewScopeSelect {
  readonly #access = inject(AccessControlService);
  readonly #viewScope = inject(ViewScopeService);

  protected readonly isHandset = select(LayoutState.isHandset);
  protected readonly allKey = ALL_KEY;
  protected readonly options = this.#access.viewScopeOptions;

  /**
   * Option labels without the repetition: a division shows its name only when it differs from the code, and a site
   * drops its division prefix under its division ("LFT – Lafayette" under DIS). Screen readers get the full site code.
   */
  protected readonly rows = computed(() =>
    this.options().map((division) => ({
      code: division.code,
      selectable: division.selectable,
      label: division.name === division.code ? division.code : `${division.code} – ${division.name}`,
      sites: division.sites.map((site) => {
        const prefix = `${division.code}-`;
        const short = site.code.startsWith(prefix) ? site.code.slice(prefix.length) : site.code;
        return { code: site.code, label: `${short} – ${site.name}`, ariaLabel: `${site.code} – ${site.name}` };
      }),
    })),
  );

  /** More than one division or site to pick from; a single-site user has nothing to narrow. */
  protected readonly hasChoices = computed(
    () => this.options().reduce((count, division) => count + (division.selectable ? 1 : 0) + division.sites.length, 0) > 1,
  );

  protected readonly value = computed(() => keyOf(this.#access.effectiveViewScope()));

  protected readonly triggerLabel = computed(() => {
    const scope = this.#access.effectiveViewScope();
    return scope.kind === "all" ? "All" : scope.code;
  });

  protected readonly hint = computed(() => {
    const scope = this.#access.effectiveViewScope();
    if (scope.kind === "all") return "Showing everything you have access to.";
    if (scope.kind === "division") return `Showing division ${scope.code}.`;
    const division = this.options().find((option) => option.sites.some((site) => site.code === scope.code));
    return `Showing site ${scope.code}. Projects, Jobs, Quotes and well sections show all of ${division?.code ?? "its division"}.`;
  });

  protected choose(key: string): void {
    const [kind, code] = key.split(":");
    this.#viewScope.select(
      kind === "division" || kind === "site" ? { kind, code } : { kind: "all" },
    );
  }
}
